/*
 * Bitbucket workspace connections: pure model behind the connect / replace-token forms, the list and the actions.
 * No React and nothing that touches `window`.
 */
import type { ScmConnection, ScmConnectionTestResult } from '@/api/schemas';
import { TOKEN_REQUIRED, TOKEN_REQUIRED_ON_REPLACE, tokenFieldError } from '@/features/integrations/credentialModel';
import type { ScmTokenKind } from './tokenGuideModel';

/** What the token needs, in one line (the connect sheet shows the full guide; the API says the same in `POST /scm-connections/{id}/test`). */
export const BITBUCKET_TOKEN_HELP =
  'A Bitbucket repository access token (any plan) or workspace access token (Premium) with Repositories: Read, plus Webhooks: Read and write for builds on push. It never leaves Hangar.';

export const SCM_CREDENTIALS_MISSING = 'This Hangar cannot store source-control tokens yet (POST /v1/credentials or /v1/scm-connections is not available).';

/* ---------- Connect ---------- */

export const WORKSPACE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,127}$/;

export interface ScmValues {
  kind: ScmTokenKind;
  name: string;
  workspace: string;
  /** Only for a repository access token. */
  repository: string;
  token: string;
}

export type ScmField = Exclude<keyof ScmValues, 'kind'>;
export type ScmErrors = Record<ScmField, string | undefined>;

export const initialScmValues: ScmValues = { kind: 'repository', name: '', workspace: '', repository: '', token: '' };

export const isBitbucketSlug = (slug: string): boolean => WORKSPACE_PATTERN.test(slug);

const SLUG_CHARS = 'Letters, digits, ".", "_", "~" and "-" only';

export function workspaceError(workspace: string): string | undefined {
  const w = workspace.trim();
  if (!w) return 'Paste the token, then choose what it reaches (or paste its Bitbucket URL)';
  return isBitbucketSlug(w) ? undefined : SLUG_CHARS;
}

/** A repository token needs its repository; a workspace token has none. */
export function repositoryError(v: Pick<ScmValues, 'kind' | 'repository'>): string | undefined {
  const r = v.repository.trim();
  if (v.kind === 'workspace') return undefined;
  if (!r) return 'Choose the repository the token reaches (or paste the repository URL)';
  return isBitbucketSlug(r) ? undefined : SLUG_CHARS;
}

/** The name defaults to the workspace (and repository), so only both empty is an error. */
export const scmNameError = (name: string, workspace: string): string | undefined => (name.trim() || workspace.trim() ? undefined : 'Name is required');

/** Validation of the connect form; a token already stored by an earlier attempt (`tokenStored`) is not asked for again. */
export const scmErrors = (v: ScmValues, tokenStored: boolean): ScmErrors => ({
  workspace: workspaceError(v.workspace),
  repository: repositoryError(v),
  name: scmNameError(v.name, v.workspace),
  token: tokenStored ? undefined : tokenFieldError(v.token, TOKEN_REQUIRED),
});

/** `workspace` or `workspace/repository`: what a connection reaches. */
export const scmTarget = (v: Pick<ScmValues, 'kind' | 'workspace' | 'repository'>): string =>
  v.kind === 'repository' && v.repository.trim() ? `${v.workspace.trim()}/${v.repository.trim()}` : v.workspace.trim();

/** The connection's name: typed, else "Bitbucket · <workspace>[/<repository>]". */
export const connectionLabel = (v: Pick<ScmValues, 'kind' | 'name' | 'workspace' | 'repository'>): string => v.name.trim() || `Bitbucket · ${scmTarget(v)}`;

export const namePlaceholder = (v: Pick<ScmValues, 'kind' | 'workspace' | 'repository'>): string => `Bitbucket · ${scmTarget(v) || 'malible'}`;

/** The `POST /scm-connections` body (`repository` only for a repository token). */
export const scmBody = (v: ScmValues, credentialId: string) => ({
  name: connectionLabel(v), workspace: v.workspace.trim(), ...(v.kind === 'repository' ? { repository: v.repository.trim() } : {}), credentialId,
});

/** Where a listed connection points: `bitbucket.org/<workspace>[/<repository>]`. */
export const connectionPath = (c: Pick<ScmConnection, 'workspace' | 'repository'>): string => `bitbucket.org/${c.workspace}${c.repository ? `/${c.repository}` : ''}`;

/* ---------- Replace token ---------- */

export const scmReplaceError = (token: string): string | undefined => tokenFieldError(token, TOKEN_REQUIRED_ON_REPLACE);

/* ---------- List ---------- */

interface Health {
  status: string;
  label: string;
}

const HEALTH: Record<ScmConnection['status'], Health> = {
  active: { status: 'succeeded', label: 'Connected' },
  degraded: { status: 'degraded', label: 'Degraded' },
  revoked: { status: 'failed', label: 'Token refused' },
};

/** The pill of a workspace: its status once it was checked, "Not checked" before, "Testing" while a test runs. */
export function connectionHealth(c: ScmConnection, testing: boolean): Health {
  if (testing) return { status: 'running', label: 'Testing' };
  return c.health.checkedAt ? HEALTH[c.status] : { status: 'queued', label: 'Not checked' };
}

/** The last error is shown only when the connection is not healthy. */
export const shownLastError = (c: ScmConnection): string | undefined => (c.status !== 'active' && c.health.lastError ? c.health.lastError : undefined);

/* ---------- Actions ---------- */

export interface ScmTestToast {
  ok: boolean;
  title: string;
  description: string | undefined;
}

/** The toast of "Test connection" for a workspace. */
export const scmTestToast = (c: Pick<ScmConnection, 'name' | 'workspace'>, r: ScmConnectionTestResult): ScmTestToast =>
  r.ok
    ? { ok: true, title: `${c.name} reachable`, description: `${r.latencyMs ?? '—'} ms · the token can list the repositories of ${c.workspace}` }
    : { ok: false, title: `${c.name} failed the connection test`, description: r.checks.find((x) => x.status === 'failed')?.message ?? undefined };

/** Why "Connect" is held back (this Hangar does not serve source control, or cannot store tokens yet), if so. */
export function scmBlockedReason(sourceControl: boolean | undefined, credentials: boolean | undefined): string | undefined {
  if (sourceControl === false) return 'This Hangar does not serve source control yet';
  return credentials === false ? 'This Hangar cannot store tokens yet' : undefined;
}
